// FR-RD14: the production `QueueFactory`/`RecoveryApi`, wiring the pure state machine
// (stateMachine.ts) to the real UploadQueue and API. Kept separate from stateMachine.ts so
// that file stays dependency-free and unit-testable with fakes.
import { api, ApiFail } from "../../api/client";
import { UploadQueue, type UploadQueueSnapshot } from "../uploadQueue";
import type { QueueFactory, RecoveryApi, RecoveryQueueHandle } from "./types";
import { RecoveryNetworkError } from "./types";

export const realRecoveryApi: RecoveryApi = {
  async getGrackleStatus(grackleId) {
    try {
      const res = await api<{ grackle_status: string; source_bytes?: number | null }>(
        "GET",
        `/uploads/${grackleId}/publish-status`,
      );
      return { status: res.grackle_status, sourceBytes: res.source_bytes ?? null };
    } catch (err) {
      if (err instanceof ApiFail && err.status === 404) return null;
      throw err;
    }
  },
};

/** A resumed/new attempt that gets no part accepted for this long is given up for *this* run and
 * left `pending_upload` for the next trigger (online event, app resume, the backoff timer, or a
 * later launch). It is a stall timer, not a deadline — reset on every accepted part — and it
 * must outlast one legitimately slow round of part PUTs (3 × 5 MiB in flight at once; the queue
 * itself times a PUT out at 5 min). At 15 s the first round rarely finished on an ordinary
 * uplink, so every attempt gave up before making progress. An `offline` event still gives up
 * at once. */
const NETWORK_GIVE_UP_MS = 6 * 60_000;

function isOffline(): boolean {
  return typeof navigator !== "undefined" && navigator.onLine === false;
}

/** `onSnapshot` gets every progress snapshot of each attempt's queue, tagged with the local
 * session id it is uploading (for the recovery notice's progress bar). */
export function createRealQueueFactory(
  onSnapshot?: (sessionId: string | undefined, snapshot: UploadQueueSnapshot) => void,
): QueueFactory {
  return (args) => {
    let resetStall: () => void = () => {};
    const queue = new UploadQueue(args.orgId, args.title, args.contentType, {
      resumeSeed: args.resumeSeed ?? undefined,
      onSessionCreated: args.onSessionCreated,
      onPartAccepted: (part) => {
        resetStall();
        args.onPartAccepted(part);
      },
      onSnapshot: (snapshot) => onSnapshot?.(args.sessionId, snapshot),
    });
    queue.start();

    const handle: RecoveryQueueHandle = {
      push: (chunk) => queue.push(chunk),
      halt: () => queue.halt(),
      finish: () => {
        if (isOffline()) {
          queue.halt();
          return Promise.reject(new RecoveryNetworkError("offline"));
        }
        return new Promise((resolve, reject) => {
          let timer: ReturnType<typeof setTimeout> | undefined;
          const cleanup = () => {
            clearTimeout(timer);
            if (typeof window !== "undefined") window.removeEventListener("offline", onOffline);
          };
          const giveUp = (reason: string) => {
            cleanup();
            // Halt rather than leave it running: a queue still alive after the state machine
            // moved on could `complete` this grackle while the next run's queue is uploading
            // the same session. Accepted parts are already persisted via onPartAccepted, so
            // the next run resumes from them.
            queue.halt();
            reject(new RecoveryNetworkError(reason));
          };
          const onOffline = () => giveUp("offline");
          if (typeof window !== "undefined") window.addEventListener("offline", onOffline);
          resetStall = () => {
            clearTimeout(timer);
            timer = setTimeout(() => giveUp("upload stalled — will retry automatically"), NETWORK_GIVE_UP_MS);
          };
          resetStall();
          queue.finish().then(
            (grackle) => {
              cleanup();
              resolve({ grackleId: grackle.id, status: grackle.status });
            },
            (err) => {
              cleanup();
              reject(err);
            },
          );
        });
      },
    };
    return handle;
  };
}
